import type {
  CameraSpecification,
  Map as MapboxMap,
  StyleSpecification,
} from 'mapbox-gl'

import { SATELLITE_STYLE_CODE, STREET_STYLE_CODE } from './mapBasemapRule'

export enum ExportBasemap {
  Street = 'street',
  Satellite = 'satellite',
  None = 'none',
}

export const BLANK_EXPORT_STYLE: StyleSpecification = {
  version: 8,
  glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
  sources: {},
  layers: [
    {
      id: 'export-blank-background',
      type: 'background',
      paint: { 'background-color': '#ffffff' },
    },
  ],
}

export const exportBasemapStyle = (
  basemap: ExportBasemap,
): string | StyleSpecification => {
  switch (basemap) {
    case ExportBasemap.Satellite:
      return SATELLITE_STYLE_CODE
    case ExportBasemap.None:
      return BLANK_EXPORT_STYLE
    default:
      return STREET_STYLE_CODE
  }
}

// Top-down through a perspective camera, 3D roofs still lean away from the centre and a raised
// point shifts off its footprint; orthographic keeps every point where the flat label engine puts it.
// A tilted view needs perspective named outright: mapbox blends orthographic toward perspective
// between 0 and 15 degrees of pitch.
export const exportCameraFor = (isTilted: boolean): CameraSpecification => ({
  'camera-projection': isTilted ? 'perspective' : 'orthographic',
})

type ImportSpecification = NonNullable<StyleSpecification['imports']>[number]
type LayerSpecification = StyleSpecification['layers'][number]
type ImportConfig = Record<string, unknown>

const withoutTerrain = (style: StyleSpecification): StyleSpecification => {
  const { terrain: _terrain, ...rest } = style
  return rest
}

const isShown = (layer: LayerSpecification): boolean =>
  !('layout' in layer && layer.layout?.visibility === 'none')

// A hidden map file stays mounted with its layers set to none, but an image source still downloads
// and decodes on add, and the render map would wait for it; only what is drawn is carried over.
const shownLayersAndSources = (
  style: StyleSpecification,
): Pick<StyleSpecification, 'layers' | 'sources'> => {
  const layers = style.layers.filter(isShown)
  const used = new Set(layers.map(layer => 'source' in layer && layer.source))
  return {
    layers,
    sources: Object.fromEntries(
      Object.entries(style.sources).filter(([id]) => used.has(id)),
    ),
  }
}

const nonNullEntries = (config: ImportConfig): ImportConfig =>
  Object.fromEntries(
    Object.entries(config).filter(([, value]) => value != null),
  )

// getStyle() serializes an import's spec, not its runtime config: setConfigProperty never reaches
// the spec, so the live values have to be written back for the copy to look like the preview.
export function exportStyleFrom(
  style: StyleSpecification,
  importConfigs: Record<string, ImportConfig | null>,
  camera: CameraSpecification,
): StyleSpecification {
  const copy: StyleSpecification = {
    ...withoutTerrain(style),
    ...shownLayersAndSources(style),
    projection: { name: 'mercator' },
    transition: { duration: 0, delay: 0 },
    camera,
  }
  if (style.imports) {
    copy.imports = style.imports.map((spec): ImportSpecification => {
      const live = importConfigs[spec.id]
      return {
        ...spec,
        ...(spec.data && { data: withoutTerrain(spec.data) }),
        ...(live && { config: { ...spec.config, ...nonNullEntries(live) } }),
      }
    })
  }
  return copy
}

export function liveImportConfigs(
  map: Pick<MapboxMap, 'getStyle' | 'getConfig'>,
): Record<string, ImportConfig | null> {
  return Object.fromEntries(
    (map.getStyle()?.imports ?? []).map(({ id }) => [
      id,
      map.getConfig(id) ?? null,
    ]),
  )
}
